import { FetchContext, FetchResponse, ofetch } from 'ofetch';

import { queryClient } from '@/app/providers/react-query-provider';
import { config } from '@/config';
import { showToast } from '@/shared/components/notification-popup';
import { getApiErrorMessage } from '@/utils/api-error-message';
import { sentryCaptureApiError } from '@/utils/sentry/sentry-api-error';

import { ApiErrorRes, TFetchError } from './api-error.types';
import {
  CONTACT_IMPORT_ACTIVE_JOB_EXISTS_CODE,
  NOT_ACCREDITED_FORBIDDEN_CODE,
} from './error-codes';
import { USER_DASHBOARD_QUERY_KEYS } from './user-dashboard/queries';

const isMeRequest = (url?: string) => url?.includes('/bff/v1/user-dashboard/profile');

const authErrorEndpoints = new Set([
  'eligibility',
  'email-advice',
  'migration',
  'sign-in-external',
  'sign-out',
]);

const isSocialAuthRequest = (url?: string): boolean => {
  if (!url) return false;
  try {
    return /\/user\/v1\/auth\/social\/(?:start|email-code\/(?:resend|confirmation))\/?$/.test(
      new URL(url, config.baseUrl).pathname,
    );
  } catch {
    return false;
  }
};

const isReferralCodeCheck = (url: string): boolean => {
  try {
    return new URL(url, config.baseUrl).pathname.endsWith('/referral/v1/codes/validate');
  } catch {
    return false;
  }
};

const shouldSuppressDefaultErrorToast = (url?: string) => {
  if (!url) {
    return false;
  }
  if (isSocialAuthRequest(url) || isReferralCodeCheck(url)) return true;

  let pathSegments: string[];
  try {
    pathSegments = new URL(url, config.baseUrl).pathname.split('/').filter(Boolean);
  } catch {
    return false;
  }

  const authSegmentIndex = pathSegments.lastIndexOf('auth');
  if (authSegmentIndex < 0 || authSegmentIndex !== pathSegments.length - 2) {
    return false;
  }

  return authErrorEndpoints.has(pathSegments[authSegmentIndex + 1]);
};

// The Import Contacts flow catches this itself (re-runs its pre-check and resumes/discards the
// blocking session) — a raw "already open" toast on top of that would just be noise/confusion.
const isContactImportActiveJobConflict = (response: FetchResponse<ApiErrorRes>) =>
  response._data?.code === CONTACT_IMPORT_ACTIVE_JOB_EXISTS_CODE;

/**
 * Role administration endpoints — entity plane (`/entity/v1`) and Gamma plane
 * (`/user/v1/gamma`, GP-3853 Task 11). Their documented error codes each get a
 * specific explanation from `getRoleAdminErrorMessage`/`getGammaRoleAdminErrorMessage`,
 * toasted by the mutation layer — so the generic toast here would only ever be
 * a duplicate.
 */
const ROLE_ADMIN_URL_PATTERN =
  /\/entity\/v1\/(roles|role-requests|permissions\/catalog|members\/[^/]+\/permissions)/;
const GAMMA_ROLE_ADMIN_URL_PATTERN =
  /\/user\/v1\/gamma\/(roles|role-requests|permissions\/catalog)/;

const isRoleAdminRequest = (url?: string) =>
  ROLE_ADMIN_URL_PATTERN.test(url ?? '') || GAMMA_ROLE_ADMIN_URL_PATTERN.test(url ?? '');

const EVENT_FLOORPLAN_URL_PATTERN = /\/events\/v\d+\/events\/[^/]+\/floorplan\/?(?:\?|$)/;

const isEventFloorplanRequest = (url?: string) => EVENT_FLOORPLAN_URL_PATTERN.test(url ?? '');

const isNotAccreditedForbidden = (response: FetchResponse<ApiErrorRes>) =>
  response.status === 403 && response._data?.code === NOT_ACCREDITED_FORBIDDEN_CODE;

const isMfaVerificationRequired = (response: FetchResponse<ApiErrorRes>) =>
  response.status === 403 && response._data?.code === 'USER.MFA.VERIFICATION_REQUIRED';

const handleUnauthorized = (response: FetchResponse<ApiErrorRes>) => {
  if (response.status !== 401) {
    return;
  }
  const url = response.url;

  if (!isMeRequest(url)) {
    queryClient.invalidateQueries({
      queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile],
    });
  } else {
    queryClient.setQueryData([USER_DASHBOARD_QUERY_KEYS.userProfile], null);
  }
};

const handleNotAccreditedForbidden = (response: FetchResponse<ApiErrorRes>) => {
  if (!isNotAccreditedForbidden(response)) {
    return;
  }
  // Resync the cached profile so the gate locks in even on a stale tab.
  queryClient.invalidateQueries({
    queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile],
  });
};

/**
 * Last `x-perm-rev` value seen on any response (GP-3853 Task 11). The backend
 * bumps this whenever a mutation could have changed the caller's effective
 * permission set — a role edited, a role reassigned — cheaper than
 * invalidating the profile on every response, since most responses carry an
 * unchanged value.
 *
 * `null` means "not primed yet": the very first value seen (per page load, or
 * after `resetPermRevTracking` runs on sign-out/session switch) has nothing to
 * compare against, so it only primes the tracker rather than counting as a
 * change — otherwise every fresh session would spuriously invalidate the
 * profile it just fetched.
 */
let lastPermRev: string | null = null;
/** Aborts every read belonging to the session being replaced; swapped on each switch. */
let sessionReads = new AbortController();
/** Resolves when the barrier lifts. Parked reads await it; see `runSessionSwitch`. */
let sessionReady = Promise.resolve();
/** Switches currently in flight. Reads stay parked while this is above zero. */
let pendingSessionSwitches = 0;
let resumeSessionReads: () => void;

/**
 * Resets the tracker so the next response's value only primes rather than
 * being compared against a value carried over from a different session. Call
 * on sign-out (`useLogout`) and on session re-mint (`useRefreshSession`) — see
 * both call sites.
 */
export const resetPermRevTracking = () => {
  lastPermRev = null;
};

/**
 * Ceiling on how long reads stay paused. Without it, a mint that never settles leaves every
 * GET in the application parked for the lifetime of the page — a worse outcome than the stale
 * cookie the barrier exists to prevent.
 */
export const SESSION_SWITCH_TIMEOUT_MS = 15_000;

/**
 * Runs a role or entity switch behind a read barrier.
 *
 * Workaround for the API refreshing the auth cookie on **every** authenticated response, not
 * only on the auth endpoints. A read belonging to the outgoing session therefore restores that
 * session's cookie whenever it resolves after the replacement has been minted, leaving the
 * client and the server disagreeing about who the caller is — observed as a 401 and as an
 * authenticated user being redirected to the login form. Retire this once a response can no
 * longer re-issue the cookie.
 *
 * The barrier both aborts reads already in flight and holds reads that start while `mintSession`
 * runs, because a read issued during the mint still carries the outgoing cookie. Writes are never
 * held: the token request itself is a POST issued from inside this call, so gating them deadlocks.
 *
 * Concurrent switches share one barrier and release it when the last of them settles, never the
 * first. Rejects — releasing the barrier — if `mintSession` fails or exceeds
 * `SESSION_SWITCH_TIMEOUT_MS`.
 */
export const runSessionSwitch = async <T>(mintSession: () => Promise<T>): Promise<T> => {
  sessionReads.abort();
  sessionReads = new AbortController();
  resetPermRevTracking();

  if (pendingSessionSwitches === 0) {
    sessionReady = new Promise<void>((resolve) => {
      resumeSessionReads = resolve;
    });
  }

  pendingSessionSwitches += 1;

  let expiry: ReturnType<typeof setTimeout>;
  try {
    return await new Promise<T>((resolve, reject) => {
      expiry = setTimeout(
        () =>
          reject(
            new Error(`Session switch did not complete within ${SESSION_SWITCH_TIMEOUT_MS}ms`),
          ),
        SESSION_SWITCH_TIMEOUT_MS,
      );
      mintSession().then(resolve, reject);
    });
  } finally {
    clearTimeout(expiry!);
    pendingSessionSwitches -= 1;
    if (pendingSessionSwitches === 0) {
      resumeSessionReads();
    }
  }
};

/**
 * `AbortSignal.any` ships in Safari 17.4, above the Safari 16 floor this app builds for.
 * Remove the manual composition once the build target moves past it.
 */
const abortOnEither = (callerSignal: AbortSignal, sessionSignal: AbortSignal): AbortSignal => {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([callerSignal, sessionSignal]);
  }

  const combined = new AbortController();
  const signals = [callerSignal, sessionSignal];
  const alreadyAborted = signals.find((signal) => signal.aborted);

  if (alreadyAborted) {
    combined.abort(alreadyAborted.reason);
    return combined.signal;
  }

  signals.forEach((signal) =>
    signal.addEventListener('abort', () => combined.abort(signal.reason), { once: true }),
  );
  return combined.signal;
};

/**
 * Cheap (one header read, one string compare) per-response check. Reads the
 * header off every response, success or error, since a permission change can
 * just as easily be observed alongside a 403 as a 200.
 *
 * Exported for direct unit testing (`index.test.ts`) — the alternative is
 * driving it through a real `ofetch` round trip with a stubbed global
 * `fetch`, which tests the `ofetch` wiring more than the tracking logic
 * itself.
 */
export const handlePermRevHeader = (response: FetchResponse<unknown>) => {
  const rev = response.headers?.get('x-perm-rev');
  if (!rev) {
    return;
  }

  if (lastPermRev !== null && rev !== lastPermRev) {
    queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
  }

  lastPermRev = rev;
};

export const defaultErrorMsg = (response: FetchResponse<ApiErrorRes>) => {
  const url = response.url;

  if (isMeRequest(url)) {
    return;
  }

  if (response.status === 401) {
    return;
  }

  if (isNotAccreditedForbidden(response)) {
    return;
  }

  if (isMfaVerificationRequired(response)) {
    return;
  }

  if (isContactImportActiveJobConflict(response)) {
    return;
  }

  if (isRoleAdminRequest(url)) {
    return;
  }

  if (isEventFloorplanRequest(url)) {
    return;
  }

  if (shouldSuppressDefaultErrorToast(url)) {
    return;
  }

  const msg = getApiErrorMessage({ data: response._data } as TFetchError, 'Something went wrong');

  showToast(msg, 'error');
};

export type ApiResponseErrorContext = FetchContext<ApiErrorRes> & {
  response: FetchResponse<ApiErrorRes>;
};

const processApiResponseError = (
  data: ApiResponseErrorContext,
  displayError?: typeof defaultErrorMsg,
) => {
  if (isSocialAuthRequest(data.response.url)) return;
  handlePermRevHeader(data.response);
  displayError?.(data.response);
  handleUnauthorized(data.response);
  handleNotAccreditedForbidden(data.response);

  sentryCaptureApiError({
    request: data.request,
    response: data.response,
    error: data.error,
    method: data.options.method,
  });
};

export const handleApiResponseError = (data: ApiResponseErrorContext) =>
  processApiResponseError(data, defaultErrorMsg);

export const handleApiResponseErrorWithoutToast = (data: ApiResponseErrorContext) =>
  processApiResponseError(data);

const baseApiFetch = ofetch.create({
  credentials: 'include',
  retry: 0,
  // Enforcement point for `runSessionSwitch`. Reads wait out a switch in progress and then carry
  // the replacement session's abort signal, so the next switch can cancel them. The loop re-reads
  // the counter because the barrier can be re-armed while a read is parked. Any caller signal is
  // preserved, or react-query could no longer cancel the request it owns.
  onRequest: async ({ options }) => {
    const method = options.method?.toUpperCase() ?? 'GET';
    if (method !== 'GET' && method !== 'HEAD') {
      return;
    }

    while (pendingSessionSwitches > 0) {
      await sessionReady;
    }
    options.signal = options.signal
      ? abortOnEither(options.signal, sessionReads.signal)
      : sessionReads.signal;
  },
  onResponse: ({ response }) => {
    handlePermRevHeader(response);
  },
  onResponseError: handleApiResponseError,
});

export const apiFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/v1',
});

export const apiUserFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/user/v1',
});

export const apiOnchainFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/onchain/v1',
});

export const apiInvestmentFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/investment/v1',
});

export const apiFileFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/file/v1',
});

export const apiEntityFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/entity/v1',
});

export const apiLookupFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/lookup/v1',
});

export const apiChatFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/chat/v1',
});

export const apiNotificationsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/notifications/v1',
});

export const apiSigningFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/signing/v1',
});

export const apiFundViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/fund-views',
});

export const apiInvestingViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/investing-views',
});

export const apiAuthFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/auth',
});

export const apiAdminViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/admin-views',
});

export const apiKanbanFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/kanban/v1',
});

// Read-only, session-scoped Deals board for the non-Gamma perspectives (Provider /
// Introducer / Buyer — GP-3839/GP-3841). Deliberately separate from
// `apiAdminViewsFetch`'s `/deal-board` routes, which are Gamma-only.
export const apiDealBoardViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/deal-board-views',
});

export const apiEntityViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/entity-views',
});

export const apiUserDashboardsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/user-dashboard',
});

export const apiIntroducerDashboardsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/introducer-dashboard',
});

export const apiReferralFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/referral/v1',
});

export const apiUserDocumentsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/user-documents',
});

export const apiDealFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/deal/v1',
});

export const apiGammaTeamFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/gamma-team',
});

export const apiDealsBffFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/deals',
});

export const apiAddressBookFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/address-book/v1',
});

export const apiAddressBookBffFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/address-book',
});

export const apiUserProfileFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/users-profile',
});

export const apiNotesViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/notes-views',
});

export const apiBffFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1',
});

// Events context (monolith) — single-context reads + all mutations.
export const apiEventsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/events/v1',
});

// Events BFF — cross-context aggregation (events + user names/emails).
export const apiEventViewsFetch = baseApiFetch.create({
  baseURL: config.baseUrl + '/bff/v1/event-views',
});
